import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const clearAuthData = () => {
  localStorage.clear();
  document.cookie.split(';').forEach(cookie => {
    document.cookie = cookie
      .replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
  });
};

const handleRefreshTokenError = () => {
  clearAuthData();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        // console.log('AuthContext: Initializing auth...');
        // console.log('AuthContext: Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
        
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('AuthContext: Session error:', sessionError);
          
          if (sessionError.message.includes('refresh_token_not_found')) {
            // console.log('AuthContext: Invalid refresh token, signing out...');
            await supabase.auth.signOut();
            handleRefreshTokenError();
            return;
          }
          throw sessionError;
        }

        if (mounted) {
          // console.log('AuthContext: Session retrieved:', session ? 'Valid session' : 'No session');
          setUser(session?.user ?? null);
          setLoading(false);
        }

        // Set up auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          // console.log('AuthContext: Auth state changed:', event);
          
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            // console.log('AuthContext: User signed in or token refreshed');
            setUser(session?.user ?? null);
          } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
            // console.log('AuthContext: User signed out or deleted');
            setUser(null);
            handleRefreshTokenError();
          }
          setLoading(false);
        });

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('AuthContext: Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setLoading(false);
          if (error instanceof Error && error.message.includes('refresh_token_not_found')) {
            handleRefreshTokenError();
          }
        }
      }
    };

    initialize();
  }, []);

  const value = {
    user,
    loading,
    signIn: async (email: string, password: string) => {
      try {
        // console.log('AuthContext: Signing in user:', email);
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        
        if (error) {
          console.error('AuthContext: Sign in error:', error);
          if (error.message.includes('refresh_token_not_found')) {
            handleRefreshTokenError();
          }
        } else {
          // console.log('AuthContext: Sign in successful');
        }
        
        return { error };
      } catch (error) {
        console.error('AuthContext: Sign in error:', error);
        if (error instanceof Error && error.message.includes('refresh_token_not_found')) {
          handleRefreshTokenError();
        }
        return { error: error as Error };
      }
    },
    signUp: async (email: string, password: string) => {
      try {
        // console.log('AuthContext: Signing up user:', email);
        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/login`
          }
        });
        if (error) throw error;
        // console.log('AuthContext: Sign up successful');
      } catch (error) {
        // console.error('AuthContext: Sign up error:', error);
        throw error;
      }
    },
    signOut: async () => {
      try {
        // console.log('AuthContext: Signing out user');
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setUser(null);
        handleRefreshTokenError();
        // console.log('AuthContext: Sign out successful');
      } catch (error) {
        // console.error('AuthContext: Sign out error:', error);
        throw error;
      }
    },
    deleteAccount: async () => {
      try {
        // console.log('AuthContext: Deleting user account');
        const { error: deleteDataError } = await supabase.rpc('delete_user_data');
        if (deleteDataError) throw deleteDataError;

        await supabase.auth.signOut();
        setUser(null);
        handleRefreshTokenError();
        // console.log('AuthContext: Account deleted successfully');
      } catch (error) {
        console.error('AuthContext: Delete account error:', error);
        throw error;
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}